import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';

const AffiliateDetail = () => {
  const { affiliateId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [affiliate, setAffiliate] = useState(null);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copyText, setCopyText] = useState('COPY');

  useEffect(() => { fetchAffiliateDetails(); }, [affiliateId]);

  const fetchAffiliateDetails = async () => {
    setIsLoading(true);
    try {
      const [affRes, leadsRes] = await Promise.all([
        adminApi.get('/leads/affiliates/list'),
        adminApi.get('/leads')
      ]);
      const aff = affRes.data?.affiliates?.find(a => a.id === affiliateId);
      setAffiliate(aff || null);
      if (aff) {
        const filteredLeads = (leadsRes.data?.leads || []).filter(l => l.source === aff.source_name);
        setLeads(filteredLeads);
      }
    } catch (e) { dispatch(showToast({ message: 'Failed to load', type: 'error' })); }
    finally { setIsLoading(false); }
  };

  const handleRegenerateKey = async () => {
    if (!confirm('Regenerate API key? Old key will stop working.')) return;
    try {
      const res = await adminApi.post(`/leads/affiliates/${affiliateId}/regenerate-key`);
      setAffiliate(res.data.affiliate);
      dispatch(showToast({ message: 'API key regenerated', type: 'success' }));
    } catch (e) { dispatch(showToast({ message: 'Failed', type: 'error' })); }
  };

  const handleToggleActive = async () => {
    try {
      const res = await adminApi.put(`/leads/affiliates/${affiliateId}`, { is_active: !affiliate.is_active });
      setAffiliate(res.data.affiliate);
      dispatch(showToast({ message: `Affiliate ${affiliate.is_active ? 'deactivated' : 'activated'}`, type: 'success' }));
    } catch (e) { dispatch(showToast({ message: 'Failed', type: 'error' })); }
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(affiliate.api_key);
    setCopyText('COPIED!');
    setTimeout(() => setCopyText('COPY'), 2000);
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-[#d97706] text-white border-[#b45309]',
      called: 'bg-[#2563eb] text-white border-[#1d4ed8]',
      follow_up: 'bg-[#7c3aed] text-white border-[#6d28d9]',
      reached: 'bg-[#0d9488] text-white border-[#0f766e]',
      account_made: 'bg-[#059669] text-white border-[#047857]',
      closed: 'bg-[#dc2626] text-white border-[#b91c1c]'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-b-[3px] ${colors[status] || 'bg-[#94a3b8] text-white border-[#64748b]'}`}>
        {status ? status.replace(/_/g, ' ') : 'PENDING'}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (isLoading) return (
    <div className="flex justify-center py-20 bg-white">
      <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  
  if (!affiliate) return (
    <div className="text-center py-20 bg-white">
      <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-user-slash text-[#94a3b8] text-2xl" />
      </div>
      <p className="text-[#475569] font-syne font-extrabold text-sm uppercase tracking-wider">Affiliate not found</p>
    </div>
  );

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 p-5 border-b-2 border-[#e2e8f0] bg-white">
        <button onClick={() => navigate('/admin/dashboard')} 
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-all">
          <i className="fas fa-arrow-left" /> BACK
        </button>
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">{affiliate.name}</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-0.5">
            FUNNEL: <span className="text-[#1e3a5f]">{affiliate.source_name}</span>
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-5 bg-white">
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-4 hover:shadow-md transition-all">
          <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">STATUS</div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-mono font-extrabold uppercase tracking-wider border-2 ${
            affiliate.is_active 
              ? 'bg-[#059669] text-white border-[#047857]' 
              : 'bg-[#dc2626] text-white border-[#b91c1c]'
          }`}>
            <span className={`w-2 h-2 rounded-full ${affiliate.is_active ? 'bg-white' : 'bg-white'}`} />
            {affiliate.is_active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-4 hover:shadow-md transition-all">
          <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">TOTAL LEADS</div>
          <div className="text-2xl font-extrabold text-[#1e3a5f]">{affiliate.lead_count || leads.length}</div>
        </div>
        <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-4 col-span-2 hover:shadow-md transition-all">
          <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">API KEY</div>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl px-4 py-2.5 text-[#1e3a5f] text-[11px] font-mono font-extrabold truncate">{affiliate.api_key}</code>
            <button onClick={copyApiKey} 
              className="px-4 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
              <i className="fas fa-copy mr-1.5" /> {copyText}
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 px-5 mb-5 bg-white">
        <button onClick={handleToggleActive} 
          className={`px-5 py-2.5 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[3px] ${
            affiliate.is_active 
              ? 'bg-[#dc2626] text-white border-[#b91c1c] hover:bg-[#b91c1c]' 
              : 'bg-[#059669] text-white border-[#047857] hover:bg-[#047857]'
          }`}>
          <i className={`fas fa-power-off mr-1.5`} />
          {affiliate.is_active ? 'DEACTIVATE' : 'ACTIVATE'}
        </button>
        <button onClick={handleRegenerateKey} 
          className="px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]">
          <i className="fas fa-key mr-1.5" /> REGENERATE KEY
        </button>
      </div>

      {/* Leads Table */}
      <div className="px-5 pb-5 bg-white">
        <h3 className="font-syne text-base font-extrabold text-[#0b1e33] mb-4 uppercase tracking-tight flex items-center gap-2">
          <i className="fas fa-users text-[#1e3a5f]" />
          LEADS FROM <span className="text-[#1e3a5f]">{affiliate.source_name}</span>
          <span className="text-[11px] text-[#64748b] font-mono font-bold normal-case">({leads.length} leads)</span>
        </h3>
        {leads.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-[#e2e8f0] rounded-xl">
            <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-inbox text-[#94a3b8] text-2xl" />
            </div>
            <p className="text-[#475569] font-mono font-extrabold text-xs uppercase tracking-wider">No leads from this funnel yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border-2 border-[#e2e8f0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                  <th className="text-left px-5 py-3.5">NAME</th>
                  <th className="text-left px-5 py-3.5">EMAIL</th>
                  <th className="text-left px-5 py-3.5">PHONE</th>
                  <th className="text-left px-5 py-3.5">STATUS</th>
                  <th className="text-left px-5 py-3.5">DEPOSIT</th>
                  <th className="text-left px-5 py-3.5">CREATED</th>
                  <th className="text-left px-5 py-3.5">CLOSED</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                    <td className="px-5 py-3.5 font-syne font-extrabold text-[#0f172a] text-xs">{lead.name}</td>
                    <td className="px-5 py-3.5 text-[12px] text-[#475569] font-bold">{lead.email}</td>
                    <td className="px-5 py-3.5 text-[12px] text-[#475569] font-bold">{lead.phone}</td>
                    <td className="px-5 py-3.5">{getStatusBadge(lead.status)}</td>
                    <td className="px-5 py-3.5">
                      {lead.depositStatus === 'deposited' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">
                          <i className="fas fa-check-circle text-[8px]" /> YES
                        </span>
                      ) : (
                        <span className="text-[#94a3b8] text-[11px] font-mono font-bold">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">{formatDate(lead.createdAt)}</td>
                    <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">{lead.closedAt ? formatDate(lead.closedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDetail;